export enum ConsentCategorySource {
  TEMPLATE = "template",
  RAW_PDF = "raw_pdf",
}

export enum ConsentCategoryStatus {
  ACTIVE = "active",
  ARCHIVED = "archived",
}

export enum ConsentSignerRole {
  EMPLOYEE = "employee",
  RESIDENT = "resident",
  FAMILY_MEMBER = "family_member",
  GP_CLINICAL = "gp_clinical",
}

export enum ConsentTemplateStatus {
  ACTIVE = "active",
  ARCHIVED = "archived",
}

export enum ConsentTemplateInputType {
  TEXT = "text",
  CHECKBOX = "checkbox",
  CHECKBOX_GROUP = "checkbox_group",
  RADIO = "radio",
}

export enum ConsentDocumentStatus {
  DRAFT = "draft",
  AWAITING_SIGNATURES = "awaiting_signatures",
  COMPLETED = "completed",
  DECLINED = "declined",
  WITHDRAWN = "withdrawn",
}

export enum ConsentDocumentDisplayStatus {
  DRAFT = "draft",
  AWAITING_SIGNATURES = "awaiting_signatures",
  COMPLETED = "completed",
  DECLINED = "declined",
  WITHDRAWAL_REQUESTED = "withdrawal_requested",
  WITHDRAWN = "withdrawn",
  EXPIRED = "expired",
  REVIEW_OVERDUE = "review_overdue",
}

export enum ConsentRenewalPeriodUnit {
  DAYS = "days",
  WEEKS = "weeks",
  MONTHS = "months",
  YEARS = "years",
}

export const consentRenewalReminderDays = [7, 14, 30] as const;
export type ConsentRenewalReminderDays = (typeof consentRenewalReminderDays)[number];

export interface ConsentRenewalRule {
  period: number;
  unit: ConsentRenewalPeriodUnit;
  reminderDays: ConsentRenewalReminderDays[];
}

export enum ConsentWithdrawalRequestStatus {
  PENDING = "pending",
  CLOSED = "closed",
  PROCESSED = "processed",
}

export enum ConsentDocumentActiveStage {
  AWAITING_EMPLOYEE_SIGNATURES = "awaiting_employee_signatures",
  AWAITING_FAMILY_CONSENT = "awaiting_family_consent",
  READY_FOR_RESIDENT_PRESENTATION = "ready_for_resident_presentation",
  AWAITING_CLINICAL_SIGNATURE = "awaiting_clinical_signature",
}

export enum ConsentClinicalSigningMode {
  ON_SITE = "on_site",
  BRENNA_USER = "brenna_user",
}

export enum ConsentClinicalIdentityVerificationMethod {
  PERSONAL_KNOWLEDGE = "personal_knowledge",
  PHOTO_ID = "photo_id",
  PROFESSIONAL_REGISTRATION = "professional_registration",
}

export enum ConsentPhotoIdType {
  DRIVERS_LICENCE = "drivers_licence",
  PASSPORT = "passport",
}

export enum ConsentFamilyContactType {
  PRIMARY = "primary",
  SECONDARY = "secondary",
  OTHER = "other",
}

export enum ConsentSigningMethod {
  FAMILY_ON_SITE = "family_on_site",
}

export enum ConsentSignatoryState {
  ACTIVE = "active",
  WAITING = "waiting",
  SIGNED = "signed",
}

export enum ConsentDocumentAuditEventType {
  CREATED = "created",
  DRAFT_UPDATED = "draft_updated",
  PDF_ATTACHED = "pdf_attached",
  SENT = "sent",
  REMINDER_SENT = "reminder_sent",
  EMPLOYEE_SIGNED = "employee_signed",
  FAMILY_CONSENTED = "family_consented",
  FAMILY_DECLINED = "family_declined",
  IDENTITY_VERIFIED = "identity_verified",
  RESIDENT_CONSENTED = "resident_consented",
  RESIDENT_DECLINED = "resident_declined",
  CLINICAL_IDENTITY_VERIFIED = "clinical_identity_verified",
  CLINICAL_SIGNED = "clinical_signed",
  CLINICAL_DECLINED = "clinical_declined",
  FAMILY_IDENTITY_VERIFIED = "family_identity_verified",
  COMPLETED = "completed",
  FINAL_PDF_VIEWED = "final_pdf_viewed",
  FINAL_PDF_EMAILED = "final_pdf_emailed",
  WITHDRAWAL_REQUESTED = "withdrawal_requested",
  WITHDRAWAL_REQUEST_CLOSED = "withdrawal_request_closed",
  WITHDRAWAL_REMINDER_SENT = "withdrawal_reminder_sent",
  WITHDRAWN = "withdrawn",
  EXPIRY_REMINDER_SENT = "expiry_reminder_sent",
  EXPIRED = "expired",
  REVIEW_REMINDER_SENT = "review_reminder_sent",
  REVIEW_OVERDUE = "review_overdue",
}

export type ConsentSortOrder = "asc" | "desc";
export type ConsentInputValues = Record<string, unknown>;

export interface ConsentOrganizationSummary {
  _id: string;
  name: string;
  description?: string;
}

export interface ConsentNursingHomeSummary {
  _id: string;
  name: string;
  address?: string;
  photo?: string;
  organization?: ConsentOrganizationSummary;
}

export interface ConsentUserSummary {
  _id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: string;
  photo?: string;
  accountType?: string;
  account?: {
    position?: string;
    employeeType?: string;
    role?: string;
  };
}

export interface ConsentResidentSummary {
  _id: string;
  firstName: string;
  lastName: string;
  photo?: string;
  room?: string;
  section?: string;
  birthdate?: string;
  nursingHome?: ConsentNursingHomeSummary;
}

export interface ConsentSupportingDocument {
  _id: string;
  originalFileName: string;
  mimeType: string;
  size: number;
  uploadedAt: string;
  /** Short-lived signed view link. Category responses include it; document lists omit it. */
  url?: string;
}

export interface ConsentCategory {
  _id: string;
  name: string;
  source: ConsentCategorySource;
  templateId?: string;
  requiredSignerRoles: ConsentSignerRole[];
  signingOrderRequired: boolean;
  signingOrder: ConsentSignerRole[];
  /**
   * Stored employee roles that may sign. A non-empty list means any employee
   * with one of these roles can complete the employee step.
   */
  employeeSignerRoles?: string[] | null;
  status: ConsentCategoryStatus;
  /** Always an array on category responses. Missing values are treated as none. */
  supportingDocuments?: ConsentSupportingDocument[];
  withdrawalNotificationEmail?: string | null;
  withdrawnNotificationEmail?: string | null;
  completedNotificationEmail?: string | null;
  declinedNotificationEmail?: string | null;
  escalationNotificationEmail?: string | null;
  expiryRule?: ConsentRenewalRule | null;
  reviewRule?: ConsentRenewalRule | null;
  expiryNotificationEmail?: string | null;
  reviewNotificationEmail?: string | null;
  organization: ConsentOrganizationSummary;
  createdAt: string;
  updatedAt: string;
}

export interface ConsentCategoryMutationRequest {
  name: string;
  source: ConsentCategorySource;
  templateId?: string;
  requiredSignerRoles: ConsentSignerRole[];
  signingOrderRequired: boolean;
  signingOrder?: ConsentSignerRole[];
  /** `null` or `[]` switches the category back to specific employees. */
  employeeSignerRoles?: string[] | null;
  withdrawalNotificationEmail?: string | null;
  withdrawnNotificationEmail?: string | null;
  completedNotificationEmail?: string | null;
  declinedNotificationEmail?: string | null;
  escalationNotificationEmail?: string | null;
  /** `null` on update removes the rule. */
  expiryRule?: ConsentRenewalRule | null;
  reviewRule?: ConsentRenewalRule | null;
  expiryNotificationEmail?: string | null;
  reviewNotificationEmail?: string | null;
}

export type CreateConsentCategoryRequest = ConsentCategoryMutationRequest;
export type UpdateConsentCategoryRequest = Partial<ConsentCategoryMutationRequest>;

export interface ConsentCategoryQuery {
  status?: ConsentCategoryStatus;
  search?: string;
  searchString?: string;
  page?: number;
  limit?: number;
  sort?: ConsentSortOrder;
}

export interface ConsentTemplateInputOption {
  id: string;
  label: string;
}

export interface ConsentTemplateInput {
  id: string;
  label: string;
  type: ConsentTemplateInputType;
  multiline?: boolean;
  options?: ConsentTemplateInputOption[];
}

export interface ConsentTemplateStructure {
  html: string;
  css: string;
  editor: {
    puck: Record<string, unknown>;
  };
}

export interface ConsentTemplate {
  _id: string;
  title: string;
  version: number;
  status: ConsentTemplateStatus;
  organization: ConsentOrganizationSummary;
  structure: ConsentTemplateStructure;
  employeeInputs: ConsentTemplateInput[];
  familyMemberInputs: ConsentTemplateInput[];
  createdAt: string;
}

export interface ConsentTemplateMutationRequest {
  title: string;
  structure: ConsentTemplateStructure;
  employeeInputs: ConsentTemplateInput[];
  familyMemberInputs: ConsentTemplateInput[];
}

export type CreateConsentTemplateRequest = ConsentTemplateMutationRequest;
export type UpdateConsentTemplateRequest = Partial<ConsentTemplateMutationRequest>;

export interface CopyConsentTemplateRequest {
  title: string;
}

export interface ConsentTemplateQuery {
  status?: ConsentTemplateStatus;
  search?: string;
  searchString?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  sort?: ConsentSortOrder;
}

export interface ConsentDocumentCategorySnapshot {
  categoryId: string;
  name: string;
  source: ConsentCategorySource;
  templateId?: string;
  requiredSignerRoles: ConsentSignerRole[];
  signingOrderRequired: boolean;
  signingOrder: ConsentSignerRole[];
  /** Copied onto the draft. Later category edits do not change an existing document. */
  employeeSignerRoles?: string[] | null;
  /** Copied from the category when the draft was created. Later category edits do not change it. */
  expiryRule?: ConsentRenewalRule | null;
  reviewRule?: ConsentRenewalRule | null;
}

export interface ConsentTemplateSnapshot {
  templateId: string;
  title: string;
  version: number;
  structure: ConsentTemplateStructure;
  employeeInputs: ConsentTemplateInput[];
  familyMemberInputs: ConsentTemplateInput[];
}

export interface ConsentDocumentFile {
  /** Internal object key. It must never be used as a browser URL. */
  fileName: string;
  originalFileName: string;
  mimeType: string;
  sha256?: string;
}

export interface ConsentDocumentPreview {
  bytes: ArrayBuffer;
  contentType: string;
}

export interface ConsentDocumentSignature {
  signerId: string;
  signerName?: string;
  signerType: ConsentSignerRole;
  acceptanceStatement: string;
  signedAt: string;
  timeZone?: string;
  employeePosition?: string;
  familyRelationship?: string;
  familyContactType?: string;
  signingMethod?: ConsentSigningMethod | string;
  facilitatedByEmployeeId?: string;
  facilitatedByEmployeeName?: string;
  facilitatedByEmployeePosition?: string;
}

export interface ResidentIdentityVerification {
  verifiedByEmployeeId: string;
  verifiedByEmployeeName: string;
  verifiedByEmployeePosition?: string;
  identityVerificationStatement: string;
  verifiedAt: string;
  timeZone?: string;
}

export interface ClinicalIdentityVerification {
  signerId: string;
  signerName: string;
  verificationMethod: ConsentClinicalIdentityVerificationMethod;
  photoIdType?: ConsentPhotoIdType;
  professionalRegistration?: string;
  verifiedByEmployeeId: string;
  verifiedByEmployeeName: string;
  verifiedByEmployeePosition?: string;
  identityVerificationStatement: string;
  verifiedAt: string;
  timeZone?: string;
}

export interface FamilyIdentityVerification {
  signerId: string;
  firstName: string;
  lastName: string;
  signerName: string;
  relationship: string;
  contactType: ConsentFamilyContactType;
  verificationMethod: ConsentClinicalIdentityVerificationMethod;
  photoIdType?: ConsentPhotoIdType;
  professionalRegistration?: string;
  verifiedByEmployeeId: string;
  verifiedByEmployeeName: string;
  verifiedByEmployeePosition?: string;
  identityVerificationStatement: string;
  verifiedAt: string;
  timeZone?: string;
}

export interface ConsentDocumentAuditEvent {
  type: ConsentDocumentAuditEventType;
  actorId?: string;
  occurredAt: string;
  mailRecordId?: string;
  recipientId?: string;
  recipientName?: string;
  signerType?: ConsentSignerRole;
  channel?: "email";
  reminderNumber?: number;
  /** Days before the expiry or review date. Present on advance renewal reminders. */
  daysBefore?: number;
}

export interface ConsentDocumentSignatory {
  id: string | null;
  name: string;
  role: ConsentSignerRole;
  signingMode?: ConsentClinicalSigningMode;
  state: ConsentSignatoryState;
  signed: boolean;
  signedAt?: string;
}

export interface ConsentWithdrawalRequest {
  id: string;
  status: ConsentWithdrawalRequestStatus;
  requestedById: string;
  requestedByName: string;
  familyRelationship?: string;
  reason: string;
  requestedAt: string;
  timeZone: string;
  resolvedById?: string;
  resolvedByName?: string;
  resolvedAt?: string;
  resolutionNote?: string;
}

export interface ConsentWithdrawal {
  withdrawnById: string;
  withdrawnByName: string;
  withdrawnByPosition?: string;
  reason: string;
  withdrawnAt: string;
  timeZone: string;
  requestId?: string;
}

export interface ConsentDocument {
  _id: string;
  title: string;
  source: ConsentCategorySource;
  categoryId?: string;
  categorySnapshot?: ConsentDocumentCategorySnapshot;
  status: ConsentDocumentStatus;
  displayStatus?: ConsentDocumentDisplayStatus;
  /** Set once the document is completed, when its category had an expiry rule. */
  expiresAt?: string;
  /** Set once the document is completed, when its category had a review rule. */
  reviewDueAt?: string;
  /** Server-computed active signing stages. */
  activeStages: ConsentDocumentActiveStage[];
  sentAt?: string | null;
  nursingHome: ConsentNursingHomeSummary;
  resident: ConsentResidentSummary;
  createdBy: ConsentUserSummary;
  /** Singular family member is retained for category-less legacy records. */
  familyMember?: ConsentUserSummary;
  familyMembers?: ConsentUserSummary[];
  familyFieldCompleter?: ConsentUserSummary;
  requiredEmployeeSigners: ConsentUserSummary[];
  clinicalSigningMode?: ConsentClinicalSigningMode;
  clinicalSigner?: ConsentUserSummary;
  clinicalIdentityVerification?: ClinicalIdentityVerification;
  /** Absent on documents created before family on-site signing. Missing means Brenna family members. */
  familySigningMode?: ConsentClinicalSigningMode;
  familyIdentityVerification?: FamilyIdentityVerification;
  templateId?: string;
  templateSnapshot?: ConsentTemplateSnapshot;
  pdf?: ConsentDocumentFile;
  /** Copied from the category at send time. Absent on drafts and on documents sent before this feature. */
  supportingDocuments?: ConsentSupportingDocument[];
  employeeInputValues: ConsentInputValues;
  familyMemberInputValues: ConsentInputValues;
  signatures: ConsentDocumentSignature[];
  residentIdentityVerification?: ResidentIdentityVerification;
  auditEvents: ConsentDocumentAuditEvent[];
  withdrawalRequests?: ConsentWithdrawalRequest[];
  withdrawal?: ConsentWithdrawal;
  /** May be absent on legacy list/detail responses. */
  signatories?: ConsentDocumentSignatory[];
  createdAt: string;
}

export interface ConsentDocumentQuery {
  status?: ConsentDocumentStatus;
  activeStage?: ConsentDocumentActiveStage;
  withdrawalRequested?: boolean;
  /** Documents whose staff display status is expired. */
  expired?: boolean;
  /** Documents whose staff display status is review overdue. */
  reviewOverdue?: boolean;
  residentId?: string;
  categoryId?: string;
  search?: string;
  searchString?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  sort?: ConsentSortOrder;
}

export interface CreateConsentDocumentRequest {
  residentId: string;
  categoryId: string;
  requiredEmployeeSignerIds?: string[];
  familyMemberIds?: string[];
  familyFieldCompleterId?: string;
  employeeInputValues?: ConsentInputValues;
  clinicalSigningMode?: ConsentClinicalSigningMode;
  clinicalSignerId?: string;
  familySigningMode?: ConsentClinicalSigningMode;
}

export type UpdateConsentDocumentRequest = Partial<CreateConsentDocumentRequest>;

export interface ConsentPdfUploadTarget {
  uploadUrl: string;
  objectKey: string;
}

export interface ConfirmConsentDocumentPdfRequest {
  objectKey: string;
  originalFileName: string;
  mimeType: "application/pdf";
}

export const EMPLOYEE_CONSENT_STATEMENT =
  "I confirm that I have completed my required review for this consent document." as const;
export const GP_CLINICAL_CONSENT_STATEMENT =
  "I have reviewed this document and agree to sign it as the GP / Clinician." as const;
export const RESIDENT_CONSENT_STATEMENT = "I agree to provide consent" as const;
export const FAMILY_CONSENT_STATEMENT =
  "I have read and understand this document. I agree to provide consent." as const;

export interface WithdrawConsentRequest {
  reason: string;
}

export interface CloseWithdrawalRequest {
  note?: string;
}

export type EmployeeConsentStatement = typeof EMPLOYEE_CONSENT_STATEMENT;
export type GpClinicalConsentStatement = typeof GP_CLINICAL_CONSENT_STATEMENT;
export type ResidentConsentStatement = typeof RESIDENT_CONSENT_STATEMENT;
export type ResidentIdentityStatement =
  `I confirm I am with ${string} and have verified their identity in person.`;

export interface EmployeeSignRequest {
  consentConfirmed: true;
  acceptanceStatement: EmployeeConsentStatement | GpClinicalConsentStatement;
}

export interface ClinicalIdentityVerificationRequest {
  signerName: string;
  verificationMethod: ConsentClinicalIdentityVerificationMethod;
  photoIdType?: ConsentPhotoIdType;
  professionalRegistration?: string;
  identityVerified: true;
  identityVerificationStatement: string;
}

export interface FamilyIdentityVerificationRequest {
  firstName: string;
  lastName: string;
  relationship: string;
  contactType: ConsentFamilyContactType;
  verificationMethod: ConsentClinicalIdentityVerificationMethod;
  photoIdType?: ConsentPhotoIdType;
  professionalRegistration?: string;
  identityVerified: true;
  identityVerificationStatement: string;
}

export interface FamilySignRequest {
  consentConfirmed: true;
  acceptanceStatement: typeof FAMILY_CONSENT_STATEMENT;
  familyMemberInputValues?: ConsentInputValues;
}

export interface ResidentConsentRequest {
  consentConfirmed: true;
  acceptanceStatement: ResidentConsentStatement;
}

export interface ResidentIdentityVerificationRequest {
  identityVerified: true;
  identityVerificationStatement: ResidentIdentityStatement;
}

export type ConsentCategoryListResponse = ConsentCategory[];
export type ConsentTemplateListResponse = ConsentTemplate[];
export type ConsentDocumentListResponse = ConsentDocument[];
